import { discussionService, ShowOnly, MessageSortOrder, MessageState } from '../services/discussionservice.js';
import { sessionService } from '../services/sessionservice.js';
import { meetingService } from '../services/meetingservice.js';
import log from '../log.js';

let Vue = null;

function vueSet(array, key, value) {
  if (Vue) {
    Vue.set(array, key, value);
  } else {
    array[key] = value;
  }
}

function vueDelete(array, key) {
  if (Vue) {
    Vue.delete(array, key);
  } else {
    delete array[key];
  }
}

const SORT_ORDER_INITIAL_VALUE = MessageSortOrder.MOST_LIKES_FIRST;

export default {
  namespaced: true,
  state: {
    sessionTopicsInfo: {},
    discussionInfo: {},
    showDiscussionWhilePolling: false,
    blockedUserList: {},
    categoryList: {}
  },
  init: function (vue, store) {
    Vue = vue;
    // we have to use this init method hack to watch data in another module
    // information about clearing meeting data is broadcast on the session list topic.. that's why it is needed in the first place
    // NOTE: currently the session list is never unsubscribed. One way to do it would be to handle it on sessionTopicsListener unsubscribe but
    //       need to be careful not to unsubscribe session list if there are still other topics within same project that have listeners..
    store.watch((state, getters) => state.projects.sessionList, map => {
      for (let session_id in map) {
        let project_id = map[session_id].projectId;
        // check if discussion module has anyone interested in this session
        if (sessionTopicsListenerCounts[session_id] && sessionTopicsListenerCounts[session_id] > 0) {
          subscribeToSessionList(project_id, store);
        }
        if (projectProfileSubscriptions[session_id] && projectProfileSubscriptions[session_id] > 0) {
          subscribeToProjectProfile(session_id, project_id, store);
        }
      }
    });
  },
  mutations: {
    sessionTopicsInfoAdded(state, info) {
      vueSet(state.sessionTopicsInfo, info.sessionId, info);
    },
    sessionTopicsInfoRemoved(state, session_id) {
      vueDelete(state.sessionTopicsInfo, session_id);
    },
    topicListUpdated(state, topics) {
      for (let topic of topics) {
        let info = state.sessionTopicsInfo[topic.sessionId];
        if (info) {
          if (topic.deletedId !== undefined) {
            vueDelete(info.topics, topic.deletedId);
          } else {
            vueSet(info.topics, topic.id, topic);
          }
        }
      }
    },
    discussionInfoAdded(state, info) {
      vueSet(state.discussionInfo, info.topicId, info);
    },
    discussionInfoRemoved(state, topic_id) {
      vueDelete(state.discussionInfo, topic_id);
    },
    messageCountUpdated(state, msg) {
      let info = state.discussionInfo[msg.topicId];
      if (info) {
        info.messageCount = msg.messageCount;
      }
    },
    messageRepliesAdded(state, data) {
      if (!data.replies || data.replies.length === 0) return;

      let info = state.discussionInfo[data.topicId];
      if (!info) return;

      data.replies.forEach(reply => {
        let existing_index = info.messages.findIndex(message => message.id === reply.replyToMessageId);
        if (existing_index >= 0) {
          info.messages[existing_index].reply = reply;
        }
      });
    },
    proposedAnswersAdded(state, data) {
      if (!data.answers || data.answers.length === 0) return;

      let info = state.discussionInfo[data.topicId];
      if (!info) return;
      if (!info.proposedAnswersCache) info.proposedAnswersCache = [];

      data.answers.forEach(proposed_answer => {
        let existing_index = info.messages.findIndex(message => message.id === proposed_answer.parentMessageId);
        if (existing_index >= 0) {
          info.messages[existing_index].proposedAnswer = proposed_answer;
        } else {
          //workaround for intermittent bug where the ProposedAnswerList arrives before the MessageList
          console.log("Caching proposed answer");
          let existing_answer_index = info.proposedAnswersCache.findIndex(answer => answer.parentMessageId === proposed_answer.parentMessageId);
          if (existing_answer_index >= 0) {
            info.proposedAnswersCache[existing_answer_index] = proposed_answer;
          } else {
            info.proposedAnswersCache.push(proposed_answer);
          }
        }
      });
    },
    messageRepliesDeleted(state, data) {
      if (!data.deletedIds || data.deletedIds.length === 0) return;

      let info = state.discussionInfo[data.topicId];
      if (!info) return;

      data.deletedIds.forEach(id => {
        let existing_index = info.messages.findIndex(message => message.reply && message.reply.id === id);
        if (existing_index >= 0) {
          info.messages[existing_index].reply = {};
        }
      });
    },
    proposedAnswersDeleted(state, data) {
      if (!data.deletedIds || data.deletedIds.length === 0) return;

      let info = state.discussionInfo[data.topicId];
      if (!info) return;

      data.deletedIds.forEach(id => {
        let existing_index = info.messages.findIndex(message => message.proposedAnswer && message.proposedAnswer.id === id);
        if (existing_index >= 0) {
          info.messages[existing_index].proposedAnswer = {};
        }
      });
    },
    adminNameUpdated(state, data) {
      let info = state.discussionInfo[data.topicId];
      let old_name = data.oldHostName;
      let new_name = data.newHostName;
      info.messages.forEach(message => {
        if (message.fromAdmin && message.senderName === old_name) {
          message.senderName = new_name;
        }
        if (message.reply && message.reply !== {} && message.reply.senderName === old_name) {
          message.reply.senderName = new_name;
        }
      });
    },
    messageListOrderUpdated(state, the_new_order) {
      let info = state.discussionInfo[the_new_order.topicId];
      if (!info) return;

      var message_to_move = null;
      for (var i = 0; i < info.messages.length; i++) {
        if (info.messages[i].id == the_new_order.messageId) {
          message_to_move = info.messages.splice(i, 1)[0];
          break;
        }
      }
      if (message_to_move != null) {
        for (var i = 0; i < info.messages.length; i++) {
          if (info.messages[i].id == the_new_order.beforeMessageId) {
            info.messages.splice(i, 0, message_to_move);
            message_to_move = null;
            break;
          }
        }
      }
      if (message_to_move != null) {
        info.messages.push(message_to_move);
      }
    },
    messageListUpdated(state, message_list) {
      let info = state.discussionInfo[message_list.topicId];
      if (!info) return;
      if (!info.proposedAnswersCache) info.proposedAnswersCache = [];

      info.messageListInitialized = true;

      let matches_filter = msg => {
        if (msg.messageState !== info.showOnly) return false;
        if (info.showOnlyFavourites && !msg.favourite) return false;
        if (info.categoryId && info.showOnly == "categorized") {
          if (info.categoryId !== msg.categoryId) return false;
        }
        if (info.labels != null && info.labels.length > 0) {
          if (msg.messageLabels == null || msg.messageLabels.length == 0) {
            return info.labels.includes(-1); //-1 means we're filtering on unlabelled messages
          }
          let msg_label_ids = msg.messageLabels.map(l => l.labelId);
          let any_label_match = info.labels.some(label => msg_label_ids.includes(label));
          if (!any_label_match) return false;
        }
        return true;
      };

      if (info.messages.length == 0) {
        info.messages = message_list.messages.filter(matches_filter);
      } else {
        for (var message of message_list.messages) {
          // handle message deleted
          if (message.deletedId !== undefined) {
            for (var i = 0; i < info.messages.length; i++) {
              if (info.messages[i].id == message.deletedId) {
                info.messages.splice(i, 1);
                break;
              }
            }
          }
          // remove message from list
          // - when moved from published to some other bucket
          // - message "unfavourited" when in showOnlyFavourites mode
          else if (!matches_filter(message)) {
              for (var i = 0; i < info.messages.length; i++) {
                if (info.messages[i].id == message.id) {
                  info.messages.splice(i, 1);
                  break;
                }
              }
            } else {
              // update existing message
              //If updated message has changed category, remove it
              var msg_updated = false;
              for (var i = 0; i < info.messages.length; i++) {
                if (info.messages[i].id == message.id) {
                  let existing_reply = info.messages[i].reply;
                  let existing_proposed_answer = info.messages[i].proposedAnswer;
                  message.reply = existing_reply;
                  message.proposedAnswer = existing_proposed_answer;
                  info.messages.splice(i, 1, message);
                  msg_updated = true;
                  break;
                }
              }
              if (!msg_updated && !(info.showOnlyFavourites && !message.favourite)) {
                // add new message (unless we are showing only favourites and this message is not favourited)
                // add message last, manually ordered messages should according to specs always be added last
                // other message sort orders will be handled by the sorting function below
                info.messages.splice(info.messages.length, 0, message);
              }
            }
        }
      }
      //apply proposed answers from cache
      if (info.messages.length > 0 && info.proposedAnswersCache.length > 0) {
        info.proposedAnswersCache.forEach(cached_answer => {
          let existing_index = info.messages.findIndex(message => message.id === cached_answer.parentMessageId);
          if (existing_index >= 0) {
            console.log("Applying proposed answer from cache");
            info.messages[existing_index].proposedAnswer = cached_answer;
          }
        });
      }
      if (info.proposedAnswersCache.length > 0) {
        console.log("Clear proposed answers from cache");
        info.proposedAnswersCache = [];
      }
      // sort items
      info.messages.sort(getMessageSortFunction(info.sortOrder));
    },
    messageLikeCountsUpdated(state, { topicId, msg }) {
      let info = state.discussionInfo[topicId];
      if (!info) return;
      for (var item of msg.messageLikeCounts) {
        for (var j = 0; j < info.messages.length; j++) {
          if (item.messageId == info.messages[j].id) {
            info.messages[j].likesCount = item.likeCount;
            break;
          }
        }
      }
      info.messages.sort(getMessageSortFunction(info.sortOrder));
    },
    userProfileUpdated(state, { sessionId, profile }) {
      var user_id = profile.userId;
      var prefix = "";
      var first_name = "";
      var last_name = "";
      var company = "";
      var capacity = "";
      if (profile.properties.prefix) {
        prefix = profile.properties.prefix.trim();
      }
      if (profile.properties.first_name) {
        first_name = profile.properties.first_name.trim();
      }
      if (profile.properties.last_name) {
        last_name = profile.properties.last_name.trim();
      }
      if (profile.properties.company) {
        company = profile.properties.company.trim();
      }
      if (profile.properties.capacity) {
        capacity = profile.properties.capacity.trim();
      }

      var user_name = ((prefix + " " + first_name).trim() + " " + last_name).trim();

      if (user_name.length > 0 && company.length > 0) {
        user_name = user_name + ", " + company;
      }
      if (user_name.length > 0 && capacity.length > 0) {
        user_name = user_name + ", " + capacity;
      }

      for (let topic_id in state.sessionTopicsInfo[sessionId].topics) {
        let info = state.discussionInfo[topic_id];
        if (info) {
          for (var j = 0; j < info.messages.length; j++) {
            if (user_id == info.messages[j].userId && info.messages[j].anonymous !== true) {
              info.messages[j].senderName = user_name;
            }
          }
        }
      }
    },
    blockedUsersListUpdated(state, { topicId, msg }) {
      if (msg.add) {
        for (var user of msg.add.users) {
          let list = state.blockedUserList[topicId];
          list.splice(0, 0, user);
        }
      } else if (msg.remove) {
        for (var user of msg.remove.users) {
          let list = state.blockedUserList[topicId];
          for (var i = 0; i < list.length; i++) {
            if (list[i].userId === user.userId) {
              list.splice(i, 1);
            }
          }
        }
      } else {
        vueSet(state.blockedUserList, topicId, msg.users);
      }
    },
    categoryListUpdated(state, { topicId, msg }) {
      if (msg.add) {
        for (var category of msg.add.categories) {
          let list = state.categoryList[topicId];
          list.splice(list.length, 0, category);
        }
      } else if (msg.remove) {
        for (var categoryId of msg.remove.categoryIds) {
          let list = state.categoryList[topicId];
          for (var i = 0; i < list.length; i++) {
            if (list[i].id === categoryId) {
              list.splice(i, 1);
            }
          }
        }
      } else if (msg.reorder) {
        let list = state.categoryList[topicId];
        var to_move = null;
        var before_index = -1;
        for (var i = 0; i < list.length; i++) {
          if (list[i].id === msg.reorder.categoryId) {
            to_move = list.splice(i, 1)[0];
            break;
          }
        }
        if (msg.reorder.beforeCategoryId) {
          for (var i = 0; i < list.length; i++) {
            if (list[i].id === msg.reorder.beforeCategoryId) {
              before_index = i;
              break;
            }
          }
        }
        if (before_index != -1) {
          list.splice(before_index, 0, to_move);
        } else {
          list.push(to_move);
        }
      } else {
        vueSet(state.categoryList, topicId, msg.categories);
      }
    },
    setMessageSortOrder(state, { topicId, sortOrder, showOnlyFavourites, showOnly, categoryId, labels, includeProposedAnswers }) {
      let info = state.discussionInfo[topicId];
      var show_only = showOnly == null ? "published" : showOnly;
      if (!info) {
        info = createNewInfo(topicId, sortOrder, showOnlyFavourites, show_only, categoryId, labels, includeProposedAnswers);
        vueSet(state.discussionInfo, info.topicId, info);
      } else if (info.sortOrder !== sortOrder || info.showOnlyFavourites != showOnlyFavourites || info.showOnly != show_only || info.labels != labels || info.includeProposedAnswers != includeProposedAnswers || typeof categoryId !== "undefined" && info.categoryId != categoryId) {
        info.messages = [];
        info.proposedAnswersCache = [];
        info.messageListInitialized = false;
        info.sortOrder = sortOrder;
        info.showOnlyFavourites = showOnlyFavourites;
        info.showOnly = show_only;
        info.categoryId = categoryId;
        info.labels = labels;
        info.includeProposedAnswers = includeProposedAnswers;
        vueSet(state.discussionInfo, info.topicId, info);
        info.resubscribe();
      }
    },
    datasetChanged(state, session_id) {
      // clear all messages from topics in sessions belonging to cleared session and resubscribe
      for (let sid in state.sessionTopicsInfo) {
        if (sid == session_id) {
          for (let topic_id in state.sessionTopicsInfo[sid].topics) {
            let info = state.discussionInfo[topic_id];
            if (info) {
              info.messages = [];
              info.resubscribe();
            }
          }
        }
      }
    },
    setShowDiscussionWhilePolling(state, params) {
      state.showDiscussionWhilePolling = params.show;
    }
  },
  getters: {
    getSessionTopics: state => session_id => {
      let item = state.sessionTopicsInfo[session_id];
      return item == null ? null : item.topics;
    },
    getDiscussionMessages: state => topic_id => {
      let item = state.discussionInfo[topic_id];
      return item == null ? null : item.messages;
    },
    getDiscussionMessageCount: state => topic_id => {
      let item = state.discussionInfo[topic_id];
      return item == null ? null : item.messageCount;
    },
    getSessionMainTopic: state => session_id => {
      let info = state.sessionTopicsInfo[session_id];
      if (info == null) return null;
      var size = Object.keys(info.topics).length;
      if (size == 0) {
        return null;
      } else if (size > 1) {
        log.warn('More than one topic found in session ' + session_id + ", using first topic");
      }
      return info.topics[Object.keys(info.topics)[0]];
    },
    getMessageSortOrder: state => topic_id => {
      let info = state.discussionInfo[topic_id];
      if (info == null) return SORT_ORDER_INITIAL_VALUE;
      return info.sortOrder;
    },
    getDiscussionInfo: state => topic_id => {
      let info = state.discussionInfo[topic_id];
      if (info == null) return {};
      return info;
    },
    getBlockedUsersList: state => topic_id => {
      let info = state.blockedUserList[topic_id];
      if (info == null) return [];
      return info;
    },
    getCategoryList: state => topic_id => {
      let info = state.categoryList[topic_id];
      if (info == null) return [];
      return info;
    }
  },
  actions: {
    addSessionTopicsListener(context, { sessionId }) {
      let info = context.state.sessionTopicsInfo[sessionId];
      let create_info = info == null;

      // create new session poll info
      if (create_info) {
        info = {
          sessionId: sessionId,
          topics: {},
          unsubscribe: null
        };
        sessionTopicsListenerCounts[sessionId] = 0;
      }

      // setup service subscription if we are not already subscribed
      if (sessionTopicsListenerCounts[sessionId] === 0) {
        // subscribe and add handlers
        let subscription = discussionService.subscribeToTopicList(sessionId, response => {
          if (response.status !== 'success') {
            context.commit('errors/addError', response, { root: true });
          }
        }, {
          'TopicList': topiclist => {
            context.commit('topicListUpdated', topiclist.topics);
          }
        });
        // create unsubscribe function
        info.unsubscribe = function () {
          sessionTopicsListenerCounts[sessionId]--;
          if (sessionTopicsListenerCounts[sessionId] === 0) {
            subscription.unsubscribe();
            context.commit('sessionTopicsInfoRemoved', sessionId);
          }
        };
      }

      // increment listener count
      sessionTopicsListenerCounts[sessionId]++;

      // subscribe to session list for detecting data clearing
      // if we don't know the project_id yet, let project module watcher subscribe later
      let project_id = context.rootGetters['projects/getProjectId'](sessionId);
      if (project_id) {
        subscribeToSessionList(project_id, context);
      }

      // add poll info to state
      if (create_info) {
        context.commit('sessionTopicsInfoAdded', info);
      }

      return {
        sessionId: sessionId,
        remove: () => info.unsubscribe()
      };
    },
    addDiscussionListener(context, { topicId, sessionId, sortOrder, showOnlyFavourites, showOnly, categoryId, labels, includeProposedAnswers }) {
      let info = context.state.discussionInfo[topicId];
      let create_info = info == null;

      // create new session poll info
      if (create_info) {
        info = createNewInfo(topicId, sortOrder ? sortOrder : SORT_ORDER_INITIAL_VALUE, showOnlyFavourites, showOnly, categoryId, labels, includeProposedAnswers);
      }

      // setup service subscription if we are not already subscribed
      if (discussionListenerCounts[topicId] === 0) {
        subscribeToDiscussion(context, topicId, info.sortOrder, info.showOnlyFavourites, showOnly, categoryId, info.labels, info.includeProposedAnswers);

        // create unsubscribe function
        info.unsubscribe = function () {
          discussionListenerCounts[topicId]--;
          if (discussionListenerCounts[topicId] === 0) {
            if (discussionSubscriptions[topicId]) {
              discussionSubscriptions[topicId].unsubscribe();
            } else {
              log.error('Could not unsubscribe from topic ' + topicId + ': subscription not found');
            }
            context.commit('discussionInfoRemoved', topicId);
          }
          let project_id = context.rootGetters['projects/getProjectId'](sessionId);
          if (project_id) {
            unsubscribeFromProjectProfile(project_id);
          }
        };
        // create resubscribe function (needed for changing sort order)
        info.resubscribe = function () {
          let topic_info = context.state.discussionInfo[topicId];
          info.subscription = subscribeToDiscussion(context, topicId, topic_info.sortOrder, topic_info.showOnlyFavourites, topic_info.showOnly, topic_info.categoryId, topic_info.labels, topic_info.includeProposedAnswers);
        };
      } else {
        //need to update sortOrder/showOnlyFavourites/showOnly, might have been changed in the subscribe
        context.commit('setMessageSortOrder', { topicId, sortOrder, showOnlyFavourites, showOnly, categoryId, labels, includeProposedAnswers });
      }

      // increment listener count
      discussionListenerCounts[topicId]++;

      // subscribe to session list for detecting data clearing
      // if we don't know the project_id yet, let project module watcher subscribe later
      let project_id = context.rootGetters['projects/getProjectId'](sessionId);
      if (project_id) {
        subscribeToProjectProfile(sessionId, project_id, context);
      } else {
        console.warn("Project id not set in projects store, cannot subscribe to profile updates!");
      }

      // add poll info to state
      if (create_info) {
        info.topicId = topicId;
        context.commit('discussionInfoAdded', info);
      }

      return {
        topicId: topicId,
        remove: () => info.unsubscribe()
      };
    },
    fetchMore(context, { topicId, sortOrder, showOnlyFavourites, showOnly, numMessages, noCutoff, labels, includeProposedAnswers }) {
      if (showOnly == null) showOnly = ShowOnly.published;
      if (numMessages == null) numMessages = 50;
      if (noCutoff == null) noCutoff = false;

      let item = context.state.discussionInfo[topicId];
      if (item == null) return;
      let messages = item.messages;

      // subscribe and add handlers
      let filter = {
        showOnly: showOnly,
        //DateTime startTime,
        numMessages: numMessages,
        numSubMessages: 0,
        showOnlyFavourites: showOnlyFavourites == null ? false : showOnlyFavourites,
        sortOrderString: sortOrder,
        labels: labels,
        includeProposedAnswers: includeProposedAnswers
      };

      if (messages.length > 0 && !noCutoff) {
        filter.cutOffId = messages[messages.length - 1].id;
      }

      return discussionService.getMessages(topicId, filter);
    }
  }
};

let sessionTopicsListenerCounts = {};
let discussionListenerCounts = {};
let discussionSubscriptions = {};
let sessionListSubscriptions = {};
let projectProfileSubscriptions = {};

function getMessageSortFunction(sort_order) {
  if (sort_order === MessageSortOrder.NEWEST_FIRST) {
    return (a, b) => {
      if (a.sentTime !== b.sentTime) return a.sentTime >= b.sentTime ? -1 : 1;
      return a.id >= b.id ? -1 : 1;
    };
  } else if (sort_order === MessageSortOrder.OLDEST_FIRST) {
    return (a, b) => {
      if (a.sentTime !== b.sentTime) return a.sentTime >= b.sentTime ? 1 : -1;
      return a.id >= b.id ? 1 : -1;
    };
  } else if (sort_order === MessageSortOrder.MOST_LIKES_FIRST) {
    return (a, b) => {
      if (a.likesCount !== b.likesCount) return a.likesCount >= b.likesCount ? -1 : 1;
      if (a.sentTime != b.sentTime) return a.sentTime >= b.sentTime ? -1 : 1;
      return a.id >= b.id ? 1 : -1;
    };
  }
}

function createNewInfo(topic_id, sort_order, showOnlyFavourites, showOnly, category_id, labels, include_proposed_answers) {
  var show_only = showOnly == null ? "published" : showOnly;
  discussionListenerCounts[topic_id] = 0;
  return {
    topicId: topic_id,
    messageListInitialized: false,
    messages: [],
    proposedAnswersCache: [],
    messageCount: {},
    sortOrder: sort_order,
    showOnlyFavourites: showOnlyFavourites,
    showOnly: show_only,
    categoryId: category_id,
    labels: labels,
    includeProposedAnswers: include_proposed_answers,
    subscription: null
  };
}

function subscribeToDiscussion(context, topic_id, sort_order, show_only_favourites, show_only, category_id, labels, include_proposed_answers) {
  // unsubscribe if we have already have a subscription
  if (discussionSubscriptions[topic_id]) {
    discussionSubscriptions[topic_id].unsubscribe();
  }

  if (show_only == null) show_only = ShowOnly.published;

  // subscribe and add handlers
  var filter = {
    MessagesFilter: {
      showOnly: show_only,
      //DateTime startTime,
      numMessages: 50,
      numSubMessages: 0,
      cutOffId: -1,
      showOnlyFavourites: show_only_favourites == null ? false : show_only_favourites,
      sortOrderString: sort_order,
      categoryId: category_id,
      labels: labels,
      includeProposedAnswers: include_proposed_answers
    }
  };
  let subscription = discussionService.subscribeToTopicMessages(topic_id, filter, response => {
    if (response.status !== 'success') {
      context.commit('errors/addError', response, { root: true });
    }
  }, {
    'MessageList': msglist => {
      msglist.topicId = topic_id;
      msglist.messages.forEach(message => {
        message.reply = {};
        if (!message.proposedAnswer) {
          message.proposedAnswer = {};
        }
      });
      context.commit('messageListUpdated', msglist);
    },
    'MessageReplyList': messageReplyList => {
      let topic_id = messageReplyList.topicId;
      let reply_list = messageReplyList.messages;
      context.commit('messageRepliesAdded', {
        topicId: topic_id,
        replies: reply_list
      });
    },
    'ProposedAnswerList': messageReplyList => {
      let topicId = messageReplyList.topicId;
      let answers = messageReplyList.answers;
      context.commit('proposedAnswersAdded', {
        topicId,
        answers
      });
    },
    'DeletedMessageReplyList': deletedMessagesList => {
      let topic_id = deletedMessagesList.topicId;
      let deleted_ids = deletedMessagesList.deletedReplyIds;
      context.commit('messageRepliesDeleted', {
        topicId: topic_id,
        deletedIds: deleted_ids
      });
    },
    'DeletedProposedAnswerList': deletedMessagesList => {
      let topic_id = deletedMessagesList.topicId;
      let deleted_ids = deletedMessagesList.deletedAnswerIds;
      context.commit('proposedAnswersDeleted', {
        topicId: topic_id,
        deletedIds: deleted_ids
      });
    },
    'MessageCount': count => {
      context.commit('messageCountUpdated', {
        topicId: topic_id,
        messageCount: count
      });
    },
    'MessageLikeCount': msg => {
      context.commit('messageLikeCountsUpdated', {
        topicId: topic_id,
        msg: {
          messageLikeCounts: [msg]
        }
      });
    },
    'MessageLikeCountList': msg => {
      context.commit('messageLikeCountsUpdated', {
        topicId: topic_id,
        msg: msg
      });
    },
    'TopicHostNameChanged': data => {
      context.commit('adminNameUpdated', data);
    },
    'BlockedUsersList': msg => {
      context.commit('blockedUsersListUpdated', {
        topicId: topic_id,
        msg: msg
      });
    },
    'BlockedUsersAdded': msg => {
      context.commit('blockedUsersListUpdated', {
        topicId: topic_id,
        msg: {
          add: msg
        }
      });
      console.log('blockadded: ' + msg);
    },
    'BlockedUsersDeleted': msg => {
      context.commit('blockedUsersListUpdated', {
        topicId: topic_id,
        msg: {
          remove: msg
        }
      });
      console.log('blockdeleted: ' + msg);
    },
    'TopicCategoryList': msg => {
      context.commit('categoryListUpdated', {
        topicId: topic_id,
        msg: msg
      });
    },
    'TopicCategoryAdditions': msg => {
      context.commit('categoryListUpdated', {
        topicId: topic_id,
        msg: {
          add: msg
        }
      });
    },
    'TopicCategoryDeletes': msg => {
      context.commit('categoryListUpdated', {
        topicId: topic_id,
        msg: {
          remove: msg
        }
      });
    },
    'MessageReordered': msg => {
      context.commit('messageListOrderUpdated', msg);
    },
    'TopicCategoryReordered': msg => {
      context.commit('categoryListUpdated', {
        topicId: topic_id,
        msg: {
          reorder: msg
        }
      });
    }
  });

  // store subscription in map
  discussionSubscriptions[topic_id] = subscription;
}

function subscribeToSessionList(project_id, context) {
  // check that we are not already subscribing to this projects session list
  if (!sessionListSubscriptions[project_id]) {
    sessionListSubscriptions[project_id] = sessionService.subscribeToSessionList(project_id, response => {
      if (response.status !== 'success') {
        context.commit('errors/addError', response, { root: true });
      }
    }, {
      'ActiveDatasetChanged': msg => {
        context.commit('discussion/datasetChanged', msg.sessionId, { root: true });
      },
      'ResultSetsDeleted': msg => {
        context.commit('discussion/datasetChanged', msg.sessionId, { root: true });
      }
    });
  }
}

function unsubscribeToSessionList(project_id) {
  if (sessionListSubscriptions[project_id]) {
    sessionListSubscriptions[project_id].unsubscribe();
    sessionListSubscriptions[project_id] = null;
  }
}

function subscribeToProjectProfile(session_id, project_id, context) {
  // check that we are not already subscribing to this projects session list
  if (!projectProfileSubscriptions[project_id]) {
    var sub = meetingService.subscribeToProjectProfile(project_id, response => {
      if (response.status !== 'success') {
        context.commit('errors/addError', response, { root: true });
      }
    }, {
      'UserProfileUpdate': msg => {
        context.commit('discussion/userProfileUpdated', { sessionId: session_id, profile: msg.profile }, { root: true });
      }
    });
    projectProfileSubscriptions[project_id] = { count: 1, subscription: sub };
  } else {
    projectProfileSubscriptions[project_id].count++;
  }
}

function unsubscribeFromProjectProfile(project_id) {
  if (projectProfileSubscriptions[project_id]) {
    if (projectProfileSubscriptions[project_id].count <= 1) {
      projectProfileSubscriptions[project_id].subscription.unsubscribe();
      projectProfileSubscriptions[project_id] = null;
    } else {
      projectProfileSubscriptions[project_id].count--;
    }
  }
}


//////////////////
// WEBPACK FOOTER
// ../ReactorJSCore/dist/store/discussion.js
// module id = null
// module chunks = 